import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { map, Observable, startWith } from 'rxjs';
import { FORM_ERRORS_FORMATTER } from '../form-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export type ValueType = boolean | null | undefined;

@Component({
  selector: 'ef-checkbox-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './checkbox-input.component.html',
  styleUrl: './checkbox-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EfCheckboxInputComponent),
      multi: true,
    },
  ],
})
@UntilDestroy()
export class EfCheckboxInputComponent implements ControlValueAccessor, AfterContentInit {
  private onChange: (value: ValueType) => void = () => {
    /**/
  };
  private onTouch: () => void = () => {
    /**/
  };

  private readonly injector = inject(Injector);

  public readonly formControlName = input<string>('');
  public readonly inputAttrs = input<Record<string, string>>({});

  public readonly checked = signal(false);
  public readonly inputEl = viewChild.required('input', { read: ElementRef });

  public isInvalid = signal<boolean>(false);
  public isTouched = signal<boolean>(false);

  public errors$?: Observable<string>;

  private readonly errorsFormatter = inject(FORM_ERRORS_FORMATTER, {
    optional: true,
  });

  @HostBinding('click')
  public onClick(): void {
    this.onTouch();
  }

  public ngAfterContentInit(): void {
    this.bindAttrs();

    const ngControl = this.injector.get(NgControl);

    if (ngControl == null || ngControl.control == null) {
      return;
    }

    const _superTouched = ngControl.control.markAsTouched;

    ngControl.control.markAsTouched = (...args) => {
      _superTouched.call(ngControl.control, ...args);

      this.isTouched.set(true);
    };

    if (this.errorsFormatter) {
      const formatter = this.errorsFormatter;

      this.errors$ = ngControl.statusChanges?.pipe(
        startWith(ngControl.errors),
        map(() => formatter.format(ngControl)),
      );
    }

    ngControl.statusChanges?.pipe(startWith(ngControl.status), untilDestroyed(this)).subscribe((status) => {
      this.isInvalid.set(status === 'INVALID');
    });
  }

  public registerOnChange(fn: (value: ValueType) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  public writeValue(checked: boolean) {
    this.checked.set(checked);
  }

  public onModelChange(value: boolean) {
    this.checked.set(value);
    this.onChange(value);
  }

  private bindAttrs() {
    const attrsToBind: Record<string, string> = {
      name: this.formControlName(),
      ...this.inputAttrs(),
    };

    for (const key in attrsToBind) {
      (this.inputEl().nativeElement as HTMLInputElement).setAttribute(key, attrsToBind[key]);
    }
  }
}
